import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { HttpContext } from '../../context/HttpContext';
import { MeContext } from '../../context/MeContext';
import { useBillingService } from '../../services/billingService';

const BillingContext = createContext({
  isLoading: true,
  error: null,
  customer: null,
  subscription: null,
  trial: null,
  paymentMethod: null,
  paymentMethods: [],
  hasPaymentMethod: false,
  plans: [],
  taxId: null,
  customPlanRequest: null,
  updateSubscription: () => {},
  updatePaymentMethod: () => {},
  billingContact: null,
  updateBillingContact: () => {},
  updateTaxId: () => {},
  removeTaxId: () => {},
  updateAllData: () => {},
  setIsLoading: () => {},
  updateIsLoading: () => {},
  refreshData: () => Promise.resolve(),
  forceRefresh: () => Promise.resolve(),
});

export const BillingProvider = ({ children }) => {
  const { authAxios } = useContext(HttpContext);
  const meCtx = useContext(MeContext);
  const billingService = useBillingService();
  const { billingEnabled } = meCtx.state;

  const [state, setState] = useState({
    customer: null,
    subscription: null,
    trial: null,
    paymentMethod: null,
    paymentMethods: [],
    hasPaymentMethod: false,
    plans: [],
    taxId: null,
    customPlanRequest: null,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const updateSubscription = useCallback((data) => {
    setState((prevState) => ({
      ...prevState,
      subscription: data,
    }));
  }, []);

  const updateTrial = useCallback((data) => {
    setState((prevState) => ({
      ...prevState,
      trial: data,
    }));
  }, []);

  const updateCustomer = useCallback((data) => {
    setState((prevState) => ({
      ...prevState,
      customer: data,
    }));
  }, []);

  const updatePaymentMethod = useCallback((data) => {
    setState((prevState) => ({
      ...prevState,
      paymentMethod: data,
      hasPaymentMethod: !!data,
    }));
  }, []);

  const updatePaymentMethods = useCallback((data) => {
    setState((prevState) => {
      const defaultMethod =
        data.find((pm) => pm.is_default) || (data.length > 0 ? data[0] : null);
      return {
        ...prevState,
        paymentMethods: data,
        paymentMethod: defaultMethod,
        hasPaymentMethod: data.length > 0,
      };
    });
  }, []);

  const getDefaultPaymentMethod = useCallback(() => {
    if (!state.paymentMethods || state.paymentMethods.length === 0) {
      return null;
    }
    return (
      state.paymentMethods.find((pm) => pm.is_default) ||
      state.paymentMethods[0]
    );
  }, [state.paymentMethods]);

  const updatePlans = useCallback((data) => {
    setState((prevState) => ({
      ...prevState,
      plans: data,
    }));
  }, []);

  const updateTaxIdData = useCallback((data) => {
    setState((prevState) => ({
      ...prevState,
      taxId: data,
    }));
  }, []);

  const updateCustomPlanRequest = useCallback((data) => {
    setState((prevState) => ({
      ...prevState,
      customPlanRequest: data,
    }));
  }, []);

  const updateAllData = useCallback(
    (data) => {
      const {
        customer,
        subscription,
        payment_method,
        payment_methods,
        has_payment_method,
        taxId,
      } = data;

      updateCustomer(customer);
      updateSubscription(subscription);
      updatePaymentMethod(payment_method);
      updateTaxIdData(taxId);

      if (payment_methods && Array.isArray(payment_methods)) {
        updatePaymentMethods(payment_methods);
      } else if (payment_method) {
        updatePaymentMethods([payment_method]);
      } else {
        updatePaymentMethods([]);
      }

      setState((prevState) => ({
        ...prevState,
        hasPaymentMethod: has_payment_method,
      }));
    },
    [
      updateCustomer,
      updateSubscription,
      updatePaymentMethod,
      updatePaymentMethods,
      updateTaxIdData,
    ]
  );

  const refreshBillingData = useCallback(async () => {
    if (!billingEnabled) {
      return null;
    }

    try {
      setIsLoading(true);
      setError(null);

      const responseCustomer = await authAxios.get(
        '/internal/v1/billing/customer'
      );
      const responseCurrentPlan = await authAxios.get(
        '/internal/v1/billing/subscriptions/current'
      );
      const responseCustomPlanRequest = await authAxios.get(
        '/internal/v1/billing/custom-plan-requests/current'
      );

      const updatedData = {
        ...responseCustomer.data,
        subscription: responseCurrentPlan.data.subscription,
      };

      updateAllData(updatedData);
      updateTrial(
        responseCurrentPlan.data.organisation ||
          responseCurrentPlan.data.trial ||
          null
      );
      updateCustomPlanRequest(
        responseCustomPlanRequest.data.custom_plan_request
      );

      return updatedData;
    } catch (err) {
      console.error('Error refreshing billing data:', err);
      setError('Failed to load billing information');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [
    authAxios,
    updateAllData,
    updateTrial,
    updateCustomPlanRequest,
    billingEnabled,
  ]);

  useEffect(() => {
    if (!billingEnabled) {
      setIsLoading(false);
      return;
    }

    const fetchInitialData = async () => {
      try {
        setError(null);

        const responseCustomer = await authAxios.get(
          '/internal/v1/billing/customer'
        );
        const responseCurrentPlan = await authAxios.get(
          '/internal/v1/billing/subscriptions/current'
        );
        const responseAllPlans = await authAxios.get(
          '/internal/v1/billing/subscription_plans'
        );
        const responseCustomPlanRequest = await authAxios.get(
          '/internal/v1/billing/custom-plan-requests/current'
        );

        const updatedData = {
          ...responseCustomer.data,
          subscription: responseCurrentPlan.data.subscription,
        };

        updateAllData(updatedData);
        updateTrial(
          responseCurrentPlan.data.organisation ||
            responseCurrentPlan.data.trial ||
            null
        );

        const plansData = Array.isArray(responseAllPlans.data)
          ? responseAllPlans.data
          : (responseAllPlans.data?.plans ?? []);
        updatePlans(plansData);
        updateCustomPlanRequest(
          responseCustomPlanRequest.data.custom_plan_request
        );
      } catch (err) {
        console.error('Error fetching initial billing data:', err);
        setError('Failed to load billing information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [
    authAxios,
    updateAllData,
    updateTrial,
    updatePlans,
    updateCustomPlanRequest,
    billingEnabled,
  ]);

  const subscribe = useCallback(
    async (planId) => {
      try {
        const response = await billingService.createSubscription(planId);

        await refreshBillingData();

        return response;
      } catch (err) {
        console.error('Error subscribing to plan:', err);
        throw err;
      }
    },
    [billingService, refreshBillingData]
  );

  const cancelSubscription = useCallback(async () => {
    try {
      if (!state.subscription) {
        throw new Error('No active subscription to cancel');
      }

      const response = await billingService.cancelSubscription(
        state.subscription.id
      );

      await refreshBillingData();

      return response;
    } catch (err) {
      console.error('Error canceling subscription:', err);
      throw err;
    }
  }, [billingService, refreshBillingData, state.subscription]);

  const resumeSubscription = useCallback(async () => {
    try {
      if (!state.subscription) {
        throw new Error('No subscription to resume');
      }

      const response = await billingService.resumeSubscription(
        state.subscription.id
      );

      await refreshBillingData();

      return response;
    } catch (err) {
      console.error('Error resuming subscription:', err);
      throw err;
    }
  }, [billingService, refreshBillingData, state.subscription]);

  const changePlan = useCallback(
    async (planId, priceId) => {
      try {
        if (!state.subscription) {
          throw new Error('No subscription to update');
        }

        const response = await billingService.updateSubscription(
          state.subscription.id,
          planId,
          priceId
        );

        await refreshBillingData();

        return response;
      } catch (err) {
        console.error('Error changing subscription plan:', err);
        throw err;
      }
    },
    [billingService, refreshBillingData, state.subscription]
  );

  const createSetupIntent = useCallback(async () => {
    try {
      const response = await billingService.createSetupIntent();
      return response;
    } catch (err) {
      console.error('Error creating setup intent:', err);
      throw err;
    }
  }, [billingService]);

  const attachPaymentMethod = useCallback(
    async (paymentMethodId, setAsDefault = true) => {
      try {
        const response = await billingService.attachPaymentMethod(
          paymentMethodId,
          setAsDefault
        );

        await refreshBillingData();

        if (
          response.payment_methods &&
          Array.isArray(response.payment_methods)
        ) {
          updatePaymentMethods(response.payment_methods);
          updatePaymentMethod(
            response.payment_methods.find((pm) => pm.is_default)
          );
        }

        return response;
      } catch (err) {
        console.error('Error attaching payment method:', err);
        throw err;
      }
    },
    [billingService, refreshBillingData, updatePaymentMethods]
  );

  const updateBillingDetails = useCallback(
    async (billingDetails) => {
      try {
        const response =
          await billingService.updateBillingDetails(billingDetails);

        updateCustomer(response.customer);

        return response;
      } catch (err) {
        console.error('Error updating billing details:', err);
        throw err;
      }
    },
    [billingService, updateCustomer]
  );

  const setDefaultPaymentMethod = useCallback(
    async (paymentMethodId) => {
      try {
        const response =
          await billingService.setDefaultPaymentMethod(paymentMethodId);

        if (
          response.payment_methods &&
          Array.isArray(response.payment_methods)
        ) {
          updatePaymentMethods(response.payment_methods);
        } else {
          await refreshBillingData();
        }

        return response;
      } catch (err) {
        console.error('Error setting default payment method:', err);
        throw err;
      }
    },
    [billingService, refreshBillingData, updatePaymentMethods]
  );

  const removePaymentMethod = useCallback(
    async (paymentMethodId) => {
      try {
        const response =
          await billingService.removePaymentMethod(paymentMethodId);

        if (
          response.payment_methods &&
          Array.isArray(response.payment_methods)
        ) {
          updatePaymentMethods(response.payment_methods);
          updatePaymentMethod(
            response.payment_methods.find((pm) => pm.is_default)
          );
        } else {
          await refreshBillingData();
        }

        return response;
      } catch (err) {
        console.error('Error removing payment method:', err);
        throw err;
      }
    },
    [billingService, refreshBillingData, updatePaymentMethods]
  );

  const getPaymentMethods = useCallback(async () => {
    try {
      const response = await billingService.getPaymentMethods();

      if (response.payment_methods && Array.isArray(response.payment_methods)) {
        updatePaymentMethods(response.payment_methods);
      }

      return response.payment_methods;
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      throw err;
    }
  }, [billingService, updatePaymentMethods]);

  const resubscribe = useCallback(
    async (planId) => {
      try {
        const response = await billingService.resubscribe(planId);

        await refreshBillingData();

        return response;
      } catch (err) {
        console.error('Error resubscribing:', err);
        throw err;
      }
    },
    [billingService, refreshBillingData]
  );

  const updateTaxId = useCallback(
    async (taxIdType, taxIdValue) => {
      try {
        const response = await billingService.updateTaxId(
          taxIdType,
          taxIdValue
        );

        await refreshBillingData();

        return response;
      } catch (err) {
        console.error('Error updating tax ID:', err);
        throw err;
      }
    },
    [billingService, refreshBillingData]
  );

  const removeTaxId = useCallback(async () => {
    try {
      const response = await billingService.removeTaxId();

      await refreshBillingData();

      return response;
    } catch (err) {
      console.error('Error removing tax ID:', err);
      throw err;
    }
  }, [billingService, refreshBillingData]);

  const acceptCustomPlan = useCallback(
    async (requestId, billingInterval) => {
      try {
        const response = await billingService.acceptCustomPlan(
          requestId,
          billingInterval
        );
        return response;
      } catch (err) {
        console.error('Error accepting custom plan:', err);
        throw err;
      }
    },
    [billingService]
  );

  const confirmCustomPlanSubscribe = useCallback(
    async (requestId, stripeSubscriptionId, success, error = null) => {
      try {
        const response = await billingService.confirmCustomPlanSubscribe(
          requestId,
          stripeSubscriptionId,
          success,
          error
        );

        await refreshBillingData();

        return response;
      } catch (err) {
        console.error('Error confirming custom plan subscription:', err);
        throw err;
      }
    },
    [billingService, refreshBillingData]
  );

  const declineCustomPlan = useCallback(
    async (requestId, rejectionReason = null) => {
      try {
        const response = await billingService.declineCustomPlan(
          requestId,
          rejectionReason
        );

        await refreshBillingData();

        return response;
      } catch (err) {
        console.error('Error declining custom plan:', err);
        throw err;
      }
    },
    [billingService, refreshBillingData]
  );

  const requestAmendment = useCallback(
    async (requestId, requestedEntitlements, notes) => {
      try {
        const response = await billingService.requestAmendment(
          requestId,
          requestedEntitlements,
          notes
        );

        await refreshBillingData();

        return response;
      } catch (err) {
        console.error('Error requesting amendment:', err);
        throw err;
      }
    },
    [billingService, refreshBillingData]
  );

  const contextValue = useMemo(
    () => ({
      isLoading,
      error,
      customer: state.customer,
      subscription: state.subscription,
      trial: state.trial,
      paymentMethod: state.paymentMethod,
      paymentMethods: state.paymentMethods,
      hasPaymentMethod: state.hasPaymentMethod,
      plans: state.plans,
      taxId: state.taxId,
      customPlanRequest: state.customPlanRequest,
      updateSubscription,
      updatePaymentMethod,
      updateAllData,
      refresh: refreshBillingData,
      subscribe,
      cancelSubscription,
      resumeSubscription,
      changePlan,
      createSetupIntent,
      attachPaymentMethod,
      updateBillingDetails,
      updateTaxId,
      removeTaxId,
      resubscribe,
      setDefaultPaymentMethod,
      removePaymentMethod,
      getPaymentMethods,
      getDefaultPaymentMethod,
      acceptCustomPlan,
      confirmCustomPlanSubscribe,
      declineCustomPlan,
      requestAmendment,
    }),
    [
      isLoading,
      error,
      state,
      updateSubscription,
      updatePaymentMethod,
      updateAllData,
      refreshBillingData,
      subscribe,
      cancelSubscription,
      resumeSubscription,
      changePlan,
      createSetupIntent,
      attachPaymentMethod,
      updateBillingDetails,
      updateTaxId,
      removeTaxId,
      resubscribe,
      setDefaultPaymentMethod,
      removePaymentMethod,
      getPaymentMethods,
      getDefaultPaymentMethod,
      acceptCustomPlan,
      confirmCustomPlanSubscribe,
      declineCustomPlan,
      requestAmendment,
    ]
  );

  return (
    <BillingContext.Provider value={contextValue}>
      {children}
    </BillingContext.Provider>
  );
};

export const useBilling = () => useContext(BillingContext);
